import React, { FunctionComponent, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Dropdown, DropdownItem } from 'plume-ui';
import {
  OutreachConfigGenericFormField,
  OutreachConfigSections,
  OutreachTriggerTypes,
  SupportOutreachTriggerFormFields,
} from '../../../../../types';
import useFormControls from '../../../hooks/useFormControls';
import FormattedMessage from '../../../../../../../utils/components/FormattedMessage';
import OutreachConfigSection from '../../../OutreachConfigSection';
import { outreachMetadataAtom } from '../../../../../../../state';
import { useRecoilValue } from 'recoil';
import { OutreachTypes } from '../../../../../../../urls';
import useValidation from '../../../hooks/useValidation';
import { useFeaturePermission } from '../../../../../../../hooks/useFeaturePermission';
import { Permission } from '../../../../../../../userPermissionConfig';
import { DropdownProperties, FormikSupportOutreachTriggerValue } from './types';
import {
  mapTriggerTypeToRenderer,
  retrieveAllowedDropdownOptionsFromMetadata,
  triggerDropdownsPossibleValues,
} from './helpers';

const FormikSupportOutreachTriggerWebhookField: FunctionComponent<OutreachConfigGenericFormField<
  FormikSupportOutreachTriggerValue
>> = ({ field, form }) => {
  const outreachMetadata = useRecoilValue(outreachMetadataAtom);
  const { t } = useTranslation();
  const titleCopyId =
    'outreach.form.supportOutreachTriggerSection.titleWebhook';
  const subtitleCopyId =
    'outreach.form.supportOutreachTriggerSection.subtitleWebhook';
  const { isSectionComplete } = useValidation();
  const { hasPermission } = useFeaturePermission();
  const canEditSupportEmail = hasPermission(
    Permission.EditSupportEmailOutreach,
  );
  const {
    setPropertyValue,
    toggleFieldValue,
    getPropertyValue,
  } = useFormControls<FormikSupportOutreachTriggerValue>(field, form);

  function renderDropdownFor<
    T extends FormikSupportOutreachTriggerValue[keyof FormikSupportOutreachTriggerValue]
  >(property: DropdownProperties) {
    const possibleValues = retrieveAllowedDropdownOptionsFromMetadata(
      property,
      field,
      outreachMetadata,
      OutreachTypes.Webhooks,
    );
    const value = field.value[property];
    let label: string = possibleValues[0].labelId;
    if (possibleValues.map(({ id }) => id).includes(value)) {
      label =
        triggerDropdownsPossibleValues[property].find(({ id }) => id === value)
          ?.labelId || label;
    } else {
      label = possibleValues[0].labelId;
      setPropertyValue(property, possibleValues[0].id as T);
    }

    return (
      <Dropdown
        label={t(label)}
        openInPortal
        expandDirection="auto"
        closeOnItemClick
      >
        {possibleValues.map((value) => (
          <DropdownItem
            key={value.id}
            onClick={() => setPropertyValue(property, value.id as T)}
          >
            <FormattedMessage id={value.labelId} />
          </DropdownItem>
        ))}
      </Dropdown>
    );
  }

  const renderTriggerContent = () => {
    const triggerType =
      field.value[SupportOutreachTriggerFormFields.TriggerType];
    const props = {
      form,
      field,
      name: field.name,
      renderDropdownFor,
      triggerType,
      outreachType: OutreachTypes.Webhooks,
    };
    const Renderer = mapTriggerTypeToRenderer[triggerType] || React.Fragment;
    return <Renderer {...props} />;
  };

  const shouldDisplayOneTimeOnlyCheckbox =
    field.value![SupportOutreachTriggerFormFields.TriggerType] ===
    OutreachTriggerTypes.Time;

  const isComplete = isSectionComplete(
    OutreachConfigSections.SupportOutreachTriggerWebhook,
  );

  return (
    <OutreachConfigSection
      title={t(titleCopyId)}
      subtitle={t(subtitleCopyId)}
      isRequired
      isComplete={isComplete}
      isOpen={canEditSupportEmail}
    >
      <div className="OutreachContainer__section-content-wrapper">
        <div className="OutreachContainer__subscriber-outreach-trigger-row">
          <div className="OutreachContainer__subscriber-outreach-trigger-label">
            <span className="color-required">* </span>
            <FormattedMessage id="outreach.form.supportOutreachTriggerSection.triggerType" />
          </div>
          <div className="OutreachContainer__subscriber-outreach-trigger-field OutreachContainer__subscriber-outreach-trigger-field--trigger-type">
            {renderDropdownFor(SupportOutreachTriggerFormFields.TriggerType)}
            {shouldDisplayOneTimeOnlyCheckbox && (
              <Checkbox
                onChange={() =>
                  toggleFieldValue(
                    SupportOutreachTriggerFormFields.IsOneTimeOnly,
                  )
                }
                checked={
                  getPropertyValue(
                    SupportOutreachTriggerFormFields.IsOneTimeOnly,
                  ) as boolean
                }
                label={t(
                  'outreach.form.supportOutreachTriggerSection.oneTimeOnly',
                )}
              />
            )}
          </div>
        </div>
        {renderTriggerContent()}
      </div>
    </OutreachConfigSection>
  );
};

export default memo(FormikSupportOutreachTriggerWebhookField);
